import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { ArrowRight, Leaf, Zap, Shield, ChevronLeft, ChevronRight } from 'lucide-react'

const Hero = () => {
  const { t } = useTranslation()
  const [currentSlide, setCurrentSlide] = useState(0)
  
  const slides = [
    {
      imageSm: 'https://images.unsplash.com/photo-1501004318641-b39e6451bec6?w=900&auto=format&fit=crop&q=70',
      imageLg: 'https://images.unsplash.com/photo-1501004318641-b39e6451bec6?w=1920&auto=format&fit=crop&q=80',
      title: t('hero.title'),
      subtitle: t('hero.subtitle')
    },
    {
      imageSm: 'https://assets.yara.com/b75a7668180a428e96161f2a6839b5d4.jpg?w=900&auto=format&fit=crop&q=70',
      imageLg: 'https://assets.yara.com/b75a7668180a428e96161f2a6839b5d4.jpg?w=1920&auto=format&fit=crop&q=80',
      title: t('hero.title'),
      subtitle: t('hero.subtitle')
    },
    {
      imageSm: 'https://assets.yara.com/5c5ee6e35e304295a450f473dd4f4c3e.jpg?w=900&auto=format&fit=crop&q=70',
      imageLg: 'https://assets.yara.com/5c5ee6e35e304295a450f473dd4f4c3e.jpg?w=1920&auto=format&fit=crop&q=80',
      title: t('hero.title'),
      subtitle: t('hero.subtitle')
    }
  ]
  
  const features = [
    {
      icon: Leaf,
      title: t('hero.features.balancedNPK'),
      description: t('hero.features.balancedNPKDesc')
    },
    {
      icon: Zap,
      title: t('hero.features.micronutrients'),
      description: t('hero.features.micronutrientsDesc')
    },
    {
      icon: Shield,
      title: t('hero.features.sustainable'),
      description: t('hero.features.sustainableDesc')
    }
  ]

  // Auto-slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 5000) // Change slide every 5 seconds

    return () => clearInterval(timer)
  }, [slides.length])

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }

  const goToSlide = (index) => {
    setCurrentSlide(index)
  }

  const [isLarge, setIsLarge] = useState(false)

  useEffect(() => {
    const onResize = () => setIsLarge(window.innerWidth >= 1024)
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const slideBg = (slide) => (isLarge ? slide.imageLg : slide.imageSm)

  return (
    <section className="relative min-h-[80vh] md:min-h-screen flex items-center justify-center overflow-hidden">
      {/* Slider Container */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url('${slideBg(slide)}')`
            }}
          >
            {/* Enhanced overlay: gradient + vignette for text contrast */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/10"></div>
              <div className="absolute inset-0 pointer-events-none" style={{
                background: 'radial-gradient(1200px 400px at 50% 100%, rgba(0,0,0,0.35), rgba(0,0,0,0) 60%)'
              }}></div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all duration-200"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6 text-white" />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all duration-200"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6 text-white" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              index === currentSlide 
                ? 'bg-white scale-125' 
                : 'bg-white/50 hover:bg-white/75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container-custom text-center text-white">
        <div className="max-w-4xl mx-auto">
          <h1 className="section-heading mb-6 text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)]">
            {slides[currentSlide].title}
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-100 max-w-2xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
            {slides[currentSlide].subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/products"
              className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center group"
            >
              {t('hero.exploreProducts')}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/mission-vision"
              className="btn-outline text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-brand-blue/90 backdrop-blur-sm"
            >
              {t('hero.ourMission')}
            </Link>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {features.map((feature, index) => {
            const Icon = feature.icon
            return (
              <div
                key={index}
                className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 border border-white border-opacity-20"
              >
                <div className="bg-olive p-3 rounded-lg w-fit mx-auto mb-4">
                  <Icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-200">{feature.description}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  )
}

export default Hero
